import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { getParsed, send } from '@/core/api/request';
import { http } from '@/core/api/http';
import { ApiError, toApiError } from '@/core/api/errors';
import { useAuthStore } from '@/features/auth/model/auth-store';
import { loadSchema } from '../model/schemas';

/**
 * `load-search/` answers with a bare array, `{ value: [...] }`, or — for `load_code` —
 * a single load object (the old tracking site relied on that last shape).
 */
const listSchema = z.union([
  z.array(loadSchema),
  z.object({ value: z.array(loadSchema) }).transform((r) => r.value),
  loadSchema.transform((l) => [l]),
]);

const SEARCH = 'company/load-search/';

async function search(params: Record<string, string>) {
  try {
    return await getParsed(SEARCH, listSchema, { params });
  } catch (e) {
    // "Nothing found" is a 404 for this endpoint, not an error state.
    if (e instanceof ApiError && e.status === 404) return [];
    throw e;
  }
}

export function useLoads() {
  const phone = useAuthStore((s) => s.phoneNumber);
  return useQuery({
    queryKey: ['loads', phone],
    enabled: Boolean(phone),
    queryFn: () => search({ phone_number: phone! }),
  });
}

export function useCancelLoad(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cardNumber: string; cardHolder: string }) =>
      send('patch', `passenger/load-cancel/${id}`, z.object({ detail: z.string() }), {
        name: v.cardHolder,
        card_number: v.cardNumber,
      }).then((r) => r.detail),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['loads'] }),
  });
}

/**
 * Public tracking by load code (the SMS link). The exact match is picked client-side so a
 * loose backend filter can't show someone else's load.
 */
export function useLoadByCode(code: string | null) {
  return useQuery({
    queryKey: ['loads', 'by-code', code],
    enabled: Boolean(code),
    retry: false,
    queryFn: async () => (await search({ load_code: code! })).find((l) => l.code === code) ?? null,
  });
}

/** Public tracking by sender/receiver mobile: every load that number is part of. */
export function useLoadsByMobile(mobile: string | null) {
  return useQuery({
    queryKey: ['loads', 'by-mobile', mobile],
    enabled: Boolean(mobile),
    retry: false,
    queryFn: () => search({ phone_number: mobile! }),
  });
}

/** Downloads the load's printable file and hands it to the browser. */
export async function downloadLoadFile(loadId: number): Promise<void> {
  try {
    const res = await http.get<Blob>(`download-load/${loadId}/`, {
      responseType: 'blob',
      headers: { Accept: '*/*' },
    });
    const disposition = String(res.headers['content-disposition'] ?? '');
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i);
    let filename = `load-${loadId}`;
    if (match?.[1]) {
      try {
        filename = decodeURIComponent(match[1]);
      } catch {
        filename = match[1];
      }
    }
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    throw toApiError(e);
  }
}
